/**
 * Keyboard shortcuts.
 *
 * Two kinds, deliberately separate:
 *
 * - BUILTIN are part of the window, not preferences. Ctrl+K opens the palette
 *   the way it does in every app that has one; nobody expects to have to bind
 *   it before it works, and there is no way to unbind it.
 *
 * - ACTIONS start unbound. You give the ones you want a key - one click on
 *   "Use recommended" does them all - and only what you have bound listens.
 *   An app that silently owns chords you never asked for eats your game's keys.
 *
 * Combos are stored as strings like "Ctrl+Shift+M" and compared against a
 * normalised description of the event, so a rebound key needs no parsing.
 */

export type BuiltinId = 'palette' | 'settings';

export type ActionId =
  | 'toggleMute' | 'toggleDeafen' | 'toggleShare' | 'leaveVoice'
  | 'createParty' | 'goHome' | 'goParties' | 'goFriends' | 'goChat' | 'goGames'
  | 'goNotifications' | 'goCrew' | 'goPlayers' | 'goBadges'
  | 'toggleSidebar' | 'toggleCrewChat' | 'focusComposer' | 'markAllRead'
  | 'zoomIn' | 'zoomOut' | 'zoomReset' | 'toggleTheme' | 'toggleStreamMode';

export type ActionGroup = 'Voice' | 'Navigation' | 'Chat' | 'View';

export type ActionDef = {
  id: ActionId;
  label: string;
  /** One line on what pressing it does. */
  description: string;
  icon: string;
  group: ActionGroup;
  /** Filled in by "Use recommended", and offered first when you record. */
  suggested: string;
  /**
   * Whether it works while Pugo is behind something else.
   *
   * Only the call controls do. Muting mid-fight is the reason a system-wide
   * hook exists; going to Friends changes a window you cannot see, so taking
   * that key from every other program would buy nothing.
   */
  global?: boolean;
};

/** Always on. Shown in settings, not rebindable. */
export const BUILTIN: Record<BuiltinId, string> = {
  palette: 'Ctrl+K',
  settings: 'Ctrl+,'
};

export const GROUPS: { id: ActionGroup; label: string; note: string }[] = [
  { id: 'Voice', label: 'Voice', note: 'These also work while you are in a game.' },
  { id: 'Navigation', label: 'Navigation', note: 'Jump anywhere in Pugo.' },
  { id: 'Chat', label: 'Chat', note: 'Messages and notifications.' },
  { id: 'View', label: 'View', note: 'Size, theme and panels.' }
];

export const ACTIONS: ActionDef[] = [
  { id: 'toggleMute',       group: 'Voice',      icon: 'micOff',      label: 'Mute microphone',        description: 'Mute or unmute yourself in a call',         suggested: 'Ctrl+Shift+M', global: true },
  { id: 'toggleDeafen',     group: 'Voice',      icon: 'headsetOff',  label: 'Deafen',                 description: 'Stop hearing the call, and stop talking',   suggested: 'Ctrl+Shift+D', global: true },
  { id: 'toggleShare',      group: 'Voice',      icon: 'monitor',     label: 'Share screen',           description: 'Start or stop sharing your screen',          suggested: 'Ctrl+Shift+S', global: true },
  { id: 'leaveVoice',       group: 'Voice',      icon: 'callLeave',   label: 'Leave voice',            description: 'Disconnect from the call you are in',        suggested: 'Ctrl+Shift+L', global: true },

  { id: 'goHome',           group: 'Navigation', icon: 'home',        label: 'Home',                   description: 'Open Home',                                  suggested: 'Ctrl+1' },
  { id: 'goParties',        group: 'Navigation', icon: 'squads',      label: 'Parties',                description: 'Open Parties',                               suggested: 'Ctrl+2' },
  { id: 'goFriends',        group: 'Navigation', icon: 'friends',     label: 'Friends',                description: 'Open Friends',                               suggested: 'Ctrl+3' },
  { id: 'goChat',           group: 'Navigation', icon: 'chat',        label: 'Chat',                   description: 'Open your conversations',                    suggested: 'Ctrl+4' },
  { id: 'goGames',          group: 'Navigation', icon: 'gamepad',     label: 'Games',                  description: 'Open Games',                                 suggested: 'Ctrl+5' },
  { id: 'goNotifications',  group: 'Navigation', icon: 'bell',        label: 'Notifications',          description: 'Open your updates',                          suggested: 'Ctrl+6' },
  { id: 'goCrew',           group: 'Navigation', icon: 'shield',      label: 'Crew',                   description: 'Open your crew',                             suggested: 'Ctrl+7' },
  { id: 'goBadges',         group: 'Navigation', icon: 'badges',      label: 'Badges',                 description: 'Open badges and leaderboards',               suggested: 'Ctrl+8' },
  { id: 'goPlayers',        group: 'Navigation', icon: 'users',       label: 'Players',                description: 'Open the player directory',                  suggested: 'Ctrl+9' },
  { id: 'createParty',      group: 'Navigation', icon: 'plus',        label: 'Create a party',         description: 'Start a new party',                          suggested: 'Ctrl+N' },

  { id: 'focusComposer',    group: 'Chat',       icon: 'edit',        label: 'Start typing',           description: 'Put the cursor in the message box',          suggested: 'Ctrl+T' },
  { id: 'markAllRead',      group: 'Chat',       icon: 'check',       label: 'Mark all read',          description: 'Clear every unread notification',            suggested: 'Shift+Escape' },
  { id: 'toggleCrewChat',   group: 'Chat',       icon: 'expand',      label: 'Crew chat',              description: 'Open or minimize crew chat',                 suggested: 'Ctrl+Shift+C' },

  { id: 'zoomIn',           group: 'View',       icon: 'plus',        label: 'Bigger',                 description: 'Make everything larger',                     suggested: 'Ctrl+=' },
  { id: 'zoomOut',          group: 'View',       icon: 'minus',       label: 'Smaller',                description: 'Make everything smaller',                    suggested: 'Ctrl+-' },
  { id: 'zoomReset',        group: 'View',       icon: 'refresh',     label: 'Normal size',            description: 'Back to 100%',                               suggested: 'Ctrl+0' },
  { id: 'toggleTheme',      group: 'View',       icon: 'sun',         label: 'Light or dark',          description: 'Switch between a light and a dark theme',    suggested: 'Ctrl+Shift+T' },
  { id: 'toggleStreamMode', group: 'View',       icon: 'eyeOff',      label: 'Stream mode',            description: 'Hide names and pictures while recording',   suggested: 'Ctrl+Shift+H' },
  { id: 'toggleSidebar',    group: 'View',       icon: 'layers',      label: 'Side panel',             description: 'Show or hide the side panel',                suggested: 'Ctrl+B' }
];

/** True for the handful of actions that are watched system-wide. */
export function isGlobalAction(id: string): boolean {
  return !!ACTIONS.find((a) => a.id === id)?.global;
}

/** Only the bindings the operating system should be watching for. */
export function globalBinds(binds: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [action, combo] of Object.entries(binds || {})) {
    if (combo && isGlobalAction(action)) out[action] = combo;
  }
  return out;
}

export function actionLabel(id: string): string {
  if (id === 'palette') return 'Search';
  if (id === 'settings') return 'Settings';
  return ACTIONS.find((a) => a.id === id)?.label ?? id;
}

/** "Ctrl+Shift+M" for an event, in the order the suggestions are written. */
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Meta');

  let key = e.key;
  if (key === ' ') key = 'Space';
  // Single letters arrive lowercase without Shift and uppercase with it;
  // normalising here is what lets "Ctrl+Shift+M" match either way.
  if (key.length === 1) key = key.toUpperCase();
  // Shift+= arrives as "+", which cannot live in a "+"-joined combo.
  if (key === '+') key = '=';
  if (['Control', 'Alt', 'Shift', 'Meta'].includes(key)) return '';

  parts.push(key);
  return parts.join('+');
}

/** The pieces of a combo, for drawing it as separate keycaps. */
export function comboParts(combo: string): string[] {
  return combo.split('+').filter(Boolean);
}

/** True while focus is somewhere that swallows plain keys. */
export function inTextField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Which bound action already uses a combo, if any.
 *
 * Built-ins count: Ctrl+K belongs to the window and cannot be given away.
 */
export function comboOwner(binds: Record<string, string>, combo: string, except?: string): string | null {
  for (const [id, c] of Object.entries(BUILTIN)) if (c === combo) return id;
  for (const [id, c] of Object.entries(binds)) if (c === combo && id !== except) return id;
  return null;
}
